import { Component, Vue, Inject, Prop, Watch } from 'vue-property-decorator';
import FieldValidationErrors from '../../components/errors/FieldValidationErrors.vue';
import { INoviNsPaymentGlobals } from '../../common/types/Payment/NoviNsPaymentGlobals';
import PhoneInput from '../../components/inputs/phone/PhoneInput.vue';
import { AchTokenData, createAchTokenData, PaymentProcessorOptions, GoogleAddressComponentNameMap, createGoogleAddressComponentNameMap, GoogleAddressComponentValueMap, createGoogleAddressComponentValueMap, AutoPayOption } from '@/common/types/AllTypesExports';
import { fiservRepo } from '@/common/repositories/FiservRepo';
import { AxiosError } from 'axios';
import NoviLogging from '@/common/services/NoviLogging';
import { getTokenizeFailureDetail } from '@/common/services/PaymentTokenizeErrorMessages';
import { PropOptions } from 'vue';
import routingNumberImage from '../../assets/routingNumber.svg';
import accountNumberImage from '../../assets/accountNumber.svg';

declare let noviNs: INoviNsPaymentGlobals;

type PhoneMapping = { [key: number]: string };

@Component({
    name: 'FiservAchEntryForm',
    components: {
        PhoneInput,
        FieldValidationErrors,
    }
})
export default class FiservAchEntryFormClass extends Vue {

    @Prop({ required: true } as PropOptions<PaymentProcessorOptions>)
    processorOptions!: PaymentProcessorOptions;

    @Prop({ required: false, default: () => true })
    validate!: boolean;

    @Prop({ required: false, default: () => false })
    locked!: boolean;

    @Prop({ required: false, default: () => false })
    saveAchOption!: boolean;

    @Prop({ required: false, default: () => false })
    forceSaveAch!: boolean;

    achData: AchTokenData = createAchTokenData();

    addressAutocomplete!: google.maps.places.Autocomplete;
    addressAutocompleteListener!: google.maps.MapsEventListener;
    mapsIsInitialized = false;

    // This object allows us to map the incoming google result keys
    addressComponentNamesMap: GoogleAddressComponentNameMap = createGoogleAddressComponentNameMap();

    // This object allows us to map the incoming google result values
    addressComponentValues: GoogleAddressComponentValueMap = createGoogleAddressComponentValueMap();

    rawPhone: string = '';
    maskedPhone: string = '';

    private stripe?: stripe.Stripe;

    @Inject('validator')
    $validator: any;

    @Watch('forceSaveAch', { immediate: true })
    onForceSaveAch(forceSave: boolean) {
        if (this.saveAchOption && forceSave)
            this.achData.saveAch = true;
    }

    maskPhone() {
        let numbers = this.maskedPhone.replace(/\D/g, ''); // Extract numbers
        let masked = '';
        let phoneFormat: PhoneMapping = {
            0: '(',
            3: ') ',
            6: '-'
        };

        // Update masked display value with each character input.
        for (let i = 0; i < numbers.length; i++) {
            masked += (phoneFormat[i] || '') + numbers[i];
        }

        this.maskedPhone = masked;
        this.achData.phone = numbers;

        // Trigger validation after updating rawPhone
        this.$validator.validate('achPhone', this.rawPhone);
    }

    get accountTypesDisplay(): { value: string; label: string }[] {
        return [
            { value: 'ECHK', label: 'Checking Account' },
            { value: 'ESAV', label: 'Savings Account' }
        ];
    }

    onAchTokenReady(token: AchTokenData) {
        Object.assign(this.achData, token);
        this.$emit('on-payment-token-ready', token);
    }

    onError(errorMsg: string) {
        this.emitError(errorMsg);
    }

    emitError(errorMsg: string) {
        this.$emit('on-error-toggle', true, errorMsg);
    }

    createPaymentToken(autoPayOptions: AutoPayOption[], billablePartyId: number) {
        this.achData.autoPayOptions = autoPayOptions;
        this.achData.billablePartyId = billablePartyId;

        // If we are passing in new ACH details then always use current payment processor
        this.achData.paymentProcessorModule = noviNs.paymentProcessor;
        this.createAchPaymentToken(this.achData);
    }

    createAchPaymentToken(achEntry: AchTokenData) {
        const achData = {
            account: `${achEntry.routingNumber}/${achEntry.accountNumber}`,
            unique: true,
        }

        return fiservRepo.getFiservPaymentToken(achData)
            .then((token: string) => {
                achEntry.achToken = token;
                this.emitPaymentToken(achEntry);
            })
            .catch((error: AxiosError) => {
                NoviLogging.capturePaymentFailure({
                    processor: 'Fiserv',
                    paymentType: 'ach',
                    code: error?.code,
                    message: error?.message,
                    hasResponse: !!error?.response,
                    httpStatus: error?.response?.status ?? null,
                    tokenizeUrl: error?.config?.url,
                    processorUrlConfigured: !!noviNs.fiservSubdomainSite,
                    responseData: error?.response?.data ?? null,
                });
                this.emitError(`There was an issue processing your ACH payment. Details: ${getTokenizeFailureDetail(error, error?.response?.data, { processor: 'Fiserv' })}`);
            });
    }

    emitPaymentToken(achEntry: AchTokenData) {

        // Do not pass sensitive ACH data to novi
        const achTokenData: AchTokenData = {
            achToken: achEntry.achToken,
            storedBankAccountId: achEntry.storedBankAccountId,
            saveAch: achEntry.saveAch,
            autoPayOptions: achEntry.autoPayOptions,
            authorizationChecked: achEntry.authorizationChecked,
            last4Digits: achEntry.accountNumber.substr(-4),
            billablePartyId: achEntry.billablePartyId,
            accountType: achEntry.accountType,
            nameOnAccount: achEntry.nameOnAccount,
            billingStreet: achEntry.billingStreet,
            billingCity: achEntry.billingCity,
            billingState: achEntry.billingState,
            billingZip: achEntry.billingZip,
            billingCountry: achEntry.billingCountry,
            paymentProcessorModule: achEntry.paymentProcessorModule,
            phone: achEntry.phone,
            email: achEntry.email,
            transactionNotes: '',
            confirmationToken: '',
            routingNumber: '',
            accountNumber: '',
            confirmAccountNumber: '',
            autoPayOptionsJson: '',
            hasPaymentAddress: false
        };

        this.$emit('on-ach-token-ready', achTokenData);
    }

    setupGoogleAutocomplete() {
        if (typeof google === 'undefined') {
            return;
        }

        const elemId = "billingStreet";
        const addressElem = document.getElementById(elemId);

        if (!addressElem) {
            return;
        }

        addressElem.addEventListener("focus", function () {
            addressElem.setAttribute("autocomplete", "nope");
        });

        this.addressAutocomplete = new google.maps.places.Autocomplete(<HTMLInputElement>addressElem, {
            types: ["geocode"],
            fields: ["address_components"],
        });
        this.addressAutocompleteListener = this.addressAutocomplete.addListener("place_changed", () => {
            const place = this.addressAutocomplete.getPlace();

            if (!place || !place.address_components) {
                return;
            }

            // Reset the current values to ensure no stale data remains
            this.addressComponentValues = createGoogleAddressComponentValueMap();

            // Map components directly
            place.address_components.forEach(addressComponent => {
                addressComponent.types.forEach(type => {
                    switch (type) {
                        case 'street_number':
                            this.addressComponentValues.street_number = addressComponent.long_name;
                            break;
                        case 'route':
                            this.addressComponentValues.route = addressComponent.long_name;
                            break;
                        case 'locality':
                            this.addressComponentValues.locality = addressComponent.long_name;
                            break;
                        case 'postal_town':
                            this.addressComponentValues.postal_town = addressComponent.long_name;
                            break;
                        case 'administrative_area_level_1':
                            this.addressComponentValues.administrative_area_level_1 = addressComponent.short_name;
                            break;
                        case 'administrative_area_level_2':
                            this.addressComponentValues.administrative_area_level_2 = addressComponent.short_name;
                            break;
                        case 'postal_code':
                            this.addressComponentValues.postal_code = addressComponent.long_name;
                            break;
                        case 'postal_code_prefix':
                            this.addressComponentValues.postal_code_prefix = addressComponent.long_name;
                            break;
                        case 'country':
                            this.addressComponentValues.country = addressComponent.short_name;
                            break;
                    }
                });
            });

            // Assign values using fallback logic
            const streetNumber = this.addressComponentValues.street_number || '';
            const route = this.addressComponentValues.route || '';
            this.achData.billingStreet = [streetNumber, route].filter(Boolean).join(' ').trim();

            this.achData.billingCity = this.addressComponentValues.locality
                || this.addressComponentValues.postal_town
                || this.addressComponentValues.administrative_area_level_2
                || '';

            this.achData.billingState = this.addressComponentValues.administrative_area_level_1 || '';

            this.achData.billingZip = this.addressComponentValues.postal_code
                || this.addressComponentValues.postal_code_prefix
                || '';
            this.achData.billingCountry = `${this.addressComponentValues.country}`;
        });

        this.mapsIsInitialized = true;
    }

    mounted() {
        const self = this;
        this.setupGoogleAutocomplete();

        // This is here as a fallback mechanism in case the google maps sdk has not been loaded when mounted() is executed
        window.addEventListener('load', () => {
            if (!this.mapsIsInitialized) {
                self.setupGoogleAutocomplete();
            }
        });

        // Initialize Routing Number Popover
        const routingNumberElement = this.$refs.popoverRoutingNumber as HTMLElement;
        ($(routingNumberElement) as any).popover({
            html: true,
            trigger: "hover",
            container: "body",
            content: `Your Routing number is the first set of numbers on the bottom of your checks.
            <img class="routing-number" src="${routingNumberImage}" alt="Routing Number Image"/>`
        });

        routingNumberElement.addEventListener("focus", () => {
            ($(routingNumberElement) as any).popover("show");
        });

        routingNumberElement.addEventListener("blur", () => {
            ($(routingNumberElement) as any).popover("hide");
        });

        // Initialize Account Number Popover
        const accountNumberElement = this.$refs.popoverAccountNumber as HTMLElement;
        ($(accountNumberElement) as any).popover({
            html: true,
            trigger: "hover",
            container: "body",
            content: `Your Account number is the second set of numbers on the bottom of your checks.
            <img class="account-number" src="${accountNumberImage}" alt="Account Number Image"/>`
        });

        accountNumberElement.addEventListener("focus", () => {
            ($(accountNumberElement) as any).popover("show");
        });

        accountNumberElement.addEventListener("blur", () => {
            ($(accountNumberElement) as any).popover("hide");
        });
    }
}